import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useCookieConsentContext } from '@/contexts/CookieConsentContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import { useLanguageAwareLink } from '@/utils/languageUtils';
import { COOKIE_CATEGORIES } from '@/types/cookies';
import { trackInternalNavigation } from '@/services/analytics';

export function CookieConsent() {
  const { t } = useTranslation();
  const location = useLocation();
  const { createLink } = useLanguageAwareLink();
  const {
    consentData,
    isPending,
    shouldShowModal,
    acceptAllCookies,
    rejectAllCookies,
    saveCustomConsent,
    closePreferences,
    showPreferences
  } = useCookieConsentContext();

  // Always show default state when modal opens (necessary=true, analytics=false)
  const getDefaultCategories = () => ({
    necessary: true,  // Always checked and disabled
    analytics: false  // Always unchecked by default
  });

  const [customCategories, setCustomCategories] = useState(getDefaultCategories());
  const [showGranularSettings, setShowGranularSettings] = useState(false);

  // Use hook's shouldShowModal function, but exclude privacy policy page from initial consent
  const shouldShow = showPreferences || (shouldShowModal() && location.pathname !== '/privacy-policy');

  // Reset to default state whenever modal opens
  React.useEffect(() => {
    if (shouldShow) {
      setCustomCategories(getDefaultCategories());
      setShowGranularSettings(false);
    }
  }, [shouldShow]);

  const handleSaveCustom = () => {
    saveCustomConsent(customCategories);
  };

  const handleCategoryToggle = (categoryId: string, enabled: boolean) => {
    setCustomCategories(prev => ({
      ...prev,
      [categoryId]: enabled
    }));
  };

  const handleCustomizeClick = () => {
    // Reset to default state when opening granular settings
    setCustomCategories(getDefaultCategories());
    setShowGranularSettings(true);
  };

  const handleClose = () => {
    if (showPreferences) {
      closePreferences();
      setShowGranularSettings(false);
    } else {
      // If there's no existing consent, store reject-all state
      // Otherwise, preserve current localStorage state
      if (isPending()) {
        rejectAllCookies();
      } else {
        // Just close the modal without changing localStorage
        closePreferences();
      }
    }
  };

  return (
    <Dialog open={shouldShow} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="sm:max-w-2xl max-h-[80vh] overflow-y-auto focus-visible:ring-0 focus-visible:ring-offset-0 [&>button]:focus:outline-none [&>button]:focus:ring-0 [&>button]:focus:ring-offset-0"
        onInteractOutside={handleClose}
        onEscapeKeyDown={handleClose}
      >
        <DialogHeader>
          <DialogTitle className="text-xl">{t('components.cookieConsent.title')}</DialogTitle>
          <DialogDescription className="text-muted-foreground leading-relaxed">
            {t('components.cookieConsent.description')}
          </DialogDescription>
          <div className="mt-3 text-sm text-muted-foreground">
            {t('components.cookieConsent.cookiePolicyText')}{' '}
            <Link
              to={createLink("/cookie-policy")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackInternalNavigation(location.pathname, createLink("/cookie-policy"), "cta")}
              className="text-primary hover:underline font-medium"
            >
              {t('pages.cookiePolicy.title')}
            </Link>
            .
          </div>
        </DialogHeader>

        {showGranularSettings && (
          <div className="space-y-6 py-4">
            {Object.entries(COOKIE_CATEGORIES).map(([categoryId, category]) => (
              <div key={categoryId} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium text-foreground">{t(`components.cookieConsent.categories.${categoryId}.name`)}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{t(`components.cookieConsent.categories.${categoryId}.description`)}</p>
                  </div>
                  <Switch
                    checked={customCategories[categoryId]}
                    onCheckedChange={(enabled) => handleCategoryToggle(categoryId, enabled)}
                    disabled={category.essential}
                    className="ml-4"
                  />
                </div>
                {category.essential && (
                  <p className="text-xs text-muted-foreground">{t('components.cookieConsent.alwaysEnabled')}</p>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-3 flex-col sm:flex-row sm:justify-between mt-6 pt-4 border-t border-border">
          {showGranularSettings ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSaveCustom}
              className="focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              {t('components.cookieConsent.savePreferences')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCustomizeClick}
              className="focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              {t('components.cookieConsent.cookieSettings')}
            </Button>
          )}
          <div className="flex gap-2 flex-col sm:flex-row">
            <Button
              variant="outline"
              size="sm"
              onClick={rejectAllCookies}
              className="focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              {t('components.cookieConsent.rejectAll')}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={acceptAllCookies}
              className="focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              {t('components.cookieConsent.acceptAll')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}